import { useState, useEffect, useCallback } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";

// Define types for the API response
interface MetaData {
  current_page: number;
  last_page: number;
}

interface APIResponse<T> {
  data: T[];
  meta: MetaData;
}

// interface PaginationParams {
//   page: number;
//   page_size: number;
// }

interface UseInfiniteScrollFetcherProps<T> {
  fetchData: (params: any) => Promise<{ data: APIResponse<T> }>;
  initialParams: any;
}

function useInfiniteScrollFetcher<T>({
  fetchData,
  initialParams,
}: UseInfiniteScrollFetcherProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [params, setParams] = useState(initialParams);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchMoreData = useCallback(async () => {
    if (loading || !hasMore) {
      return;
    }
    setLoading(true);

    try {
      const response = await fetchData(params);
      const { data, meta } = response?.data;

      setItems((prevItems) => [...prevItems, ...data]);
      setParams((prev: any) => ({ ...prev, page: prev.page + 1 }));
      setHasMore(meta.current_page < meta.last_page); // Check if more pages exist
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchData, params, hasMore, loading]);

  // New method to update params and reset
  const updateParams = useCallback(
    (newParams: Partial<typeof initialParams>) => {
      // Reset to first page when params change
      setParams((prev: any) => ({
        ...prev,
        ...newParams,
        page: 1,
      }));
      // Clear previous items
      setItems([]);
      // Reset hasMore
      setHasMore(true);
    },
    []
  );

  // Add a refetch method
  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData(initialParams);
      const { data, meta } = response?.data;

      setItems([...data]);
      setParams((prev: any) => ({ ...prev, page: prev.page + 1 }));
      setHasMore(meta.current_page < meta.last_page); // Check if more pages exist
    } catch (error) {
      console.error("Refetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchData, initialParams]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
    disabled: loading,
  });

  return { items, sentryRef, loading, updateParams, refetch };
}

export default useInfiniteScrollFetcher;
